import {COMMENT_SAVING_STATUS, COMMENTS_INPUT_MAX_LENGTH, COMMENTS_INPUT_THRESHOLD_LENGTH} from "utils/constants";
import {InlineLoader, InlineLoaderState} from "components/common/InlineLoader.component";
import {useTranslation} from "components/translations/useTranslation";
import type {CommentsValidationProps} from "types/taskDetailsComments";

/**
 * The state of the draft comment under the comment field: that it is a draft, that it is saving or
 * saved, or why it is not saved (offline, too long, the save failed), and a warning near the limit.
 */
export const CommentsValidation = ({comment, offline, savingStatus}: CommentsValidationProps) => {
    const {translate} = useTranslation();
    const length = comment?.length ?? 0;
    const characterMaxLimitReached = length > COMMENTS_INPUT_MAX_LENGTH;
    const characterThresholdReached = length >= COMMENTS_INPUT_THRESHOLD_LENGTH && length <= COMMENTS_INPUT_MAX_LENGTH;

    const draftInfo = (
        <InlineLoader
            state={InlineLoaderState.Info}
            text={translate("taskDetail.draftComment")}
            tooltip={{
                id: "draftCommentInfoTooltip",
                content: () => translate("taskDetail.draftCommentCanBeSaved"),
            }}
        />
    );

    if (!comment) {
        return draftInfo;
    }

    if (offline) {
        return (
            <InlineLoader
                state={InlineLoaderState.Error}
                text={translate("taskDetail.draftCommentNotSaved")}
                tooltip={{
                    id: "commentNotDeliveredTooltip",
                    content: () => translate("taskDetail.commentNotDeliveredTooltip"),
                }}
            />
        );
    }

    if (characterMaxLimitReached) {
        return (
            <InlineLoader
                state={InlineLoaderState.Error}
                text={translate("taskDetail.draftCommentsCharacters")}
                tooltip={{
                    id: "commentTooLongTooltip",
                    content: () => translate("taskDetail.commentTooLongTooltip"),
                }}
            />
        );
    }

    if (characterThresholdReached) {
        return (
            <InlineLoader
                state={InlineLoaderState.Warning}
                text={translate("taskDetail.draftCommentsCharactersLeft", length)}
            />
        );
    }

    switch (savingStatus) {
        case COMMENT_SAVING_STATUS.SAVING:
            return <InlineLoader state={InlineLoaderState.Active} text={translate("taskDetail.draftCommentUpdating")}/>;
        case COMMENT_SAVING_STATUS.DONE:
            return <InlineLoader state={InlineLoaderState.Completed} text={translate("taskDetail.draftCommentTextUpdated")}/>;
        case COMMENT_SAVING_STATUS.ERROR:
            return <InlineLoader state={InlineLoaderState.Error} text={translate("taskDetail.draftCommentError")}/>;
        default:
            return draftInfo;
    }
};

export default CommentsValidation;
